import { useState, useMemo } from "react";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { DayNavigator } from "@/components/dashboard/day-navigator";
import { BlockListView } from "@/components/blocks/block-list-view";
import { CommuteList } from "@/components/commutes/commute-list";
import { PeriodSummary } from "@/components/period-summary";
import {
  formatLocalDateAsUTC,
  getPreviousDay,
  getNextDay,
} from "@/lib/date-utils";
import { useSettings } from "@/hooks/use-settings";
import { calculateFuelCost, calculateNetEarnings } from "@/lib/fuel-calculations";

export default function DailyPage() {
  const [currentDate, setCurrentDate] = useState(new Date());

  const dateStr = formatLocalDateAsUTC(currentDate);

  // Daily stats
  const dailyData = useQuery(api.dashboard.getDailyStats, {
    date: dateStr,
  });

  // Daily blocks with shifts for mileage
  const dailyBlocks = useQuery(api.blocks.listBlocksWithShiftsByDateRange, {
    startDate: dateStr,
    endDate: dateStr,
  });

  // Settings and fuel price for net profit calculation
  const { settings } = useSettings();
  const latestFuelPrice = useQuery(api.expenses.getLatestFuelPrice);

  // Commutes for the day
  const commutes = useQuery(api.shifts.getCommutesByDate, {
    date: dateStr,
  });

  // Calculate commute stats
  const commuteStats = useMemo(() => {
    if (!commutes || commutes.length === 0) return null;

    let totalDuration = 0; // ms
    let totalMiles = 0;

    for (const commute of commutes) {
      const duration =
        (commute.endedAt ?? Date.now()) -
        commute.startedAt -
        (commute.pauseDuration || 0);
      totalDuration += duration;
      totalMiles += commute.totalMiles;
    }

    // Calculate fuel cost if we have settings
    let fuelCost: number | null = null;
    if (settings?.mpg && latestFuelPrice && totalMiles > 0) {
      fuelCost = calculateFuelCost(totalMiles, settings.mpg, latestFuelPrice);
    }

    return {
      count: commutes.length,
      totalDurationMinutes: Math.round(totalDuration / 60000),
      totalMiles,
      fuelCost,
    };
  }, [commutes, settings?.mpg, latestFuelPrice]);

  // Calculate daily net profit after estimated fuel costs
  const dailyNetProfit = useMemo(() => {
    if (!dailyBlocks || !settings?.mpg || !latestFuelPrice) return null;

    let totalEarnings = 0;
    let totalFuelCost = 0;
    let totalMiles = 0;

    for (const block of dailyBlocks) {
      const earnings = block.basePay + block.tip + block.noRoutePayout;
      totalEarnings += earnings;

      const miles = block.shift?.totalMiles ?? block.manualMiles ?? 0;
      if (miles > 0) {
        totalMiles += miles;
        totalFuelCost += calculateFuelCost(miles, settings.mpg, latestFuelPrice);
      }
    }

    return {
      totalEarnings,
      totalFuelCost,
      totalMiles,
      netProfit: calculateNetEarnings(totalEarnings, totalFuelCost),
    };
  }, [dailyBlocks, settings?.mpg, latestFuelPrice]);

  const handlePreviousDay = () => {
    setCurrentDate(getPreviousDay(currentDate));
  };

  const handleNextDay = () => {
    setCurrentDate(getNextDay(currentDate));
  };

  const stats = dailyData?.stats;

  // Build fuel estimate for PeriodSummary
  const fuelEstimate = dailyNetProfit && dailyNetProfit.totalMiles > 0 ? {
    fuelCost: dailyNetProfit.totalFuelCost,
    mpg: settings?.mpg ?? 0,
    fuelPrice: latestFuelPrice ?? 0,
  } : undefined;

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl space-y-6 pb-24">
      {/* Page Title */}
      <div>
        <h1 className="text-2xl font-bold">Daily Overview</h1>
        <p className="text-muted-foreground">Track your daily earnings and progress</p>
      </div>

      {/* Day navigation */}
      <DayNavigator
        currentDate={currentDate}
        onPreviousDay={handlePreviousDay}
        onNextDay={handleNextDay}
      />

      {/* Daily Summary with integrated fuel estimate */}
      <PeriodSummary
        period="daily"
        stats={stats ? {
          blockCount: stats.blockCount,
          totalEarnings: stats.totalEarnings,
          totalMinutes: stats.totalMinutes,
          actualMinutes: stats.actualMinutes ?? undefined,
          totalMiles: stats.totalMiles,
          avgHourlyRate: stats.avgHourlyRate,
          actualHourlyRate: stats.actualHourlyRate ?? undefined,
          avgPerMileRate: stats.avgPerMileRate,
        } : null}
        fuelEstimate={fuelEstimate}
      />

      {/* Daily blocks section */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Blocks</h2>

        <BlockListView
          blocks={dailyBlocks}
          showFilters={false}
          emptyMessage="No blocks on this day"
        />
      </div>

      {/* Commutes section */}
      {commuteStats && commutes && commutes.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Commutes</h2>
          <CommuteList commutes={commutes} stats={commuteStats} />
        </div>
      )}
    </div>
  );
}
